import React, { useRef, useEffect, useMemo, useCallback, useState, forwardRef, useImperativeHandle, ForwardRefRenderFunction } from 'react';
import styled from 'styled-components/macro';
import { hasItems } from '../../utils/collections';
import { List } from '@fluentui/react/lib/List';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import * as _ from "lodash";
import * as Actions from "../../store/actions";
import { platformActions } from '../../communication/Communication';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { User } from '../../Moderation';
import Avatar from './Avatar';
import { App } from '../../app';

export interface PersonPickerHandles {
  focus: () => void;
}

export interface Person {
  name: string;
  profileImageUrl?: string;
  userInfoUrl: string;
}

export interface PersonPickerProps {
  relevantPersons?: Person[];
  searchPersons?: (filter: string) => Person[];
  filterText: string;
  widthPx: number;
  onItemSelected: (person: Person) => void;
  onDismiss: () => void;
}

const pageSize = 50;
const searchDebounceDelayMSec = 1000;

const PersonPicker: ForwardRefRenderFunction<PersonPickerHandles, PersonPickerProps> = (props: PersonPickerProps, ref): JSX.Element => {
  const [searchedPersons, setSearchedPersons] = useState<Person[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const refFirstPerson = useRef<any>();
  const refWrapper = useRef<HTMLDivElement>();

  useImperativeHandle(ref, () => ({
    focus: () => {
      refFirstPerson.current?.focus();
    }
  }));


  const relevantPersons = useMemo(
    () => (props.relevantPersons || []).filter(x => x.name && x.name.toLowerCase().indexOf(props.filterText.toLowerCase()) !== -1)
    , [props.relevantPersons, props.filterText]);

  const onSearchRequestedDebounced = useCallback(
    _.debounce((filterText: string) => {
      const url = `${Actions.Apis.UserPropertiesList}?q=${filterText}&pageSize=${pageSize}`;

      setIsSearching(true);
      platformActions.get(false, url)
        .then(x => {
          setSearchedPersons((x?.data ?? []).map((y: User) => ({
            name: y.displayName,
            userInfoUrl: App.GetUserProfileLink(y.principalId),
            profileImageUrl: y.profileImageUrl
          } as Person)));
          setIsSearching(false);
        })
        .catch(err => {
          setSearchedPersons([]);
          setIsSearching(false);
        });
    }, searchDebounceDelayMSec)
    , []);

  useEffect(() => {
    if (props.filterText.length !== 0) {
      onSearchRequestedDebounced(props.filterText);
    } else {
      setSearchedPersons([]);
    }
  }, [props.filterText]);

  const filteredItems = useMemo(
    () => [
      ...relevantPersons,
      ...searchedPersons.filter(x =>
        x.name.toLowerCase().indexOf(props.filterText.toLowerCase()) !== -1
        && !relevantPersons.some(s => s.name === x.name)
      )
    ]
    , [relevantPersons, searchedPersons]
  );

  const onRenderCell = (person: Person, index: number, isScrolling: boolean): JSX.Element => {
    return (
      <li
        ref={index === 0 ? refFirstPerson : null}
        data-is-focusable={true}
        tabIndex={0}
        onClick={() => props?.onItemSelected?.(person)}
        onKeyDown={(e) => {
          const keyCode = e.keyCode ?? e.which;
          if (keyCode === KeyCodes.escape) {
            props?.onDismiss?.();
          }
        }}
      >
        <Avatar className='avatar-logo' name={person.name} sizeInRems={2.8} fontSizeInRems={1.2} />
        <span css="margin-left: .6rem">{person.name}</span>
      </li>
    );
  };

  return <Wrapper ref={refWrapper} widthPx={props.widthPx} isHidden={!hasItems(filteredItems) && !isSearching && !props.filterText}>
    {hasItems(filteredItems) && <ListContainer>
      <FocusZone direction={FocusZoneDirection.vertical}>
        <div className="list-container">
          <List
            items={filteredItems}
            onRenderCell={onRenderCell}
          />
        </div>
        {isSearching && <Searching showSeparator>
          <Spinner size={SpinnerSize.small} />
          <span>Searching...</span>
        </Searching>}
      </FocusZone>
    </ListContainer>}

    {!hasItems(filteredItems) && isSearching && (
      <Searching>
        <Spinner size={SpinnerSize.small} />
        <span>Searching...</span>
      </Searching>
    )}

    {!hasItems(filteredItems) && !isSearching && <NoMatches>No matches found</NoMatches>}
  </Wrapper>
}

const Wrapper = styled.div<{ widthPx: number, isHidden?: boolean }>`
    display: flex;
    flex-direction: column;
    background-color: white;
    border: solid 1px #999999;
    box-shadow: 0.2rem 0.2rem #000000 20% 0.1rem;
    padding: .6rem;
    width: ${props => props.widthPx}px;
    visibility: ${props => props.isHidden ? 'hidden' : 'visible'};
    margin-top: 0.5rem;

    >* + * {
        margin-top: 2rem;
    }

    .list-container {
      max-height: 30rem;
      overflow-y: auto;
      padding: .2rem .4rem;
    }
`;

const ListContainer = styled.ul`
  display: flex;
  flex-direction: column;

  li {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: .2rem;
    font-size: 1.2rem;

    img {
      border-radius: 50%;
      width: 2.4rem;
    }
  }

  li + li {
    margin-top: .3rem;
  }

  img {
    width: 1.6rem;
    margin-right: .8rem;
  }
`;

const Searching = styled.div<{ showSeparator?: boolean }>`
  display: flex;
  align-items: center;
  padding: .6rem .4rem .4rem .4rem;
  margin-top: ${props => props.showSeparator ? '.5rem' : '0'};
  border-top: ${props => props.showSeparator ? 'solid 1px var(--color-on-surface-lightest)' : 'none'};
  color: var(--color-primary);

  >* + * {
    margin-left: .8rem;
  }
`;

const NoMatches = styled.div`
  padding: .6rem .4rem .4rem .4rem;
`;

export default forwardRef(PersonPicker);
